import React, { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line
import { useDrag, useDrop } from 'react-dnd';
import EditIcon from '@mui/icons-material/Edit';
import { Tooltip } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import { useSearchParams } from 'react-router-dom';
import Spinner from 'Components/Spinner';
import {
  Checkbox,
  TableCell,
  TableHeaderCell,
  SquareCheckbox,
} from './CustomDesigns';
import AlterImage from '../../../../Assets/alter-image.png';
import styles from '../index.module.css';

const MacTooltip = withStyles({
  tooltip: {
    backgroundColor: '#ffffff',
    color: '#1d1d1f',
    fontSize: 11,
    fontWeight: 400,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
    border: '1px solid rgba(0, 0, 0, 0.18)',
    borderRadius: 3,
    padding: '2px 6px',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    margin: '2px 0 0 0',
  },
  popper: {
    pointerEvents: 'none',
  },
})(Tooltip);

const TruncatedCardTitle = ({ title, className }) => {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [anchorWidth, setAnchorWidth] = useState(0);
  const titleText = title || '';

  useEffect(() => {
    const checkTruncation = () => {
      const el = ref.current;
      if (!el) return;
      setIsTruncated(
        el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth
      );
      setAnchorWidth(el.offsetWidth);
    };
    checkTruncation();
    const id = window.setTimeout(checkTruncation, 0);
    window.addEventListener('resize', checkTruncation);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [titleText]);

  return (
    <MacTooltip
      title={isTruncated ? titleText : ''}
      placement="bottom-start"
      enterDelay={400}
      enterNextDelay={200}
      PopperProps={{
        style: anchorWidth ? { maxWidth: anchorWidth, width: anchorWidth } : undefined,
        modifiers: {
          flip: { enabled: false },
          preventOverflow: { enabled: true, boundariesElement: 'viewport' },
        },
      }}
    >
      <h3 ref={ref} className={className}>
        {titleText}
      </h3>
    </MacTooltip>
  );
};

// A meta pill (e.g. category) that ellipsis-clips long text so it can't widen
// the card / break the meta grid alignment, and reveals the full value in a
// tooltip on hover when clipped.
const TruncatedChip = ({ text, className, maxWidth = 150 }) => {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const chipText = text || '';

  useEffect(() => {
    const checkTruncation = () => {
      const el = ref.current;
      if (!el) return;
      setIsTruncated(el.scrollWidth > el.clientWidth);
    };
    checkTruncation();
    const id = window.setTimeout(checkTruncation, 0);
    window.addEventListener('resize', checkTruncation);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [chipText]);

  return (
    <MacTooltip
      title={isTruncated ? chipText : ''}
      placement="bottom-start"
      enterDelay={400}
      enterNextDelay={200}
    >
      <span className={className}>
        <span
          ref={ref}
          style={{
            display: 'block',
            maxWidth,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {chipText}
        </span>
      </span>
    </MacTooltip>
  );
};

const DraggableVideoCard = ({
  video,
  index,
  isSelected,
  onSelect,
  onCardClick,
  onViewPhotos,
  tabTitle,
  isItemSelected,
  toggleItemSelection,
  setEditItems,
  setEditItemsStatus,
  renderContent,
  changeDateFormat,
  selectedItems, // Add selectedItems prop to access all selected items
  isDragActive, // New prop to track if any drag is active
  setIsDragActive, // Function to set drag active state
  canReorder, // Enable drag-to-reorder within grid
  moveCard, // Callback to reorder items: moveCard(dragIndex, hoverIndex)
  onReorderEnd, // Callback when reorder drag ends (auto-save)
}) => {
  const {
    content_id,
    video_id,
    draft_content_id,
    title,
    description,
    video_description,
    thumbnail,
    thumbnails,
    category_name,
    category,
    content_status,
    status,
    publish_date,
    created,
    video_size,
    draft_id,
    transcoder_status,
    workflow_status,
  } = video;

  const [searchParams] = useSearchParams();
  const isFromGift = searchParams.get('fromGift') === 'true';

  const videoId = tabTitle === 2 ? draft_content_id : content_id || video_id;
  const videoTitle = title || 'Untitled';
  const videoDescription = description || video_description || '';
  const videoThumbnail = Array.isArray(thumbnail)
    ? thumbnail[0] || AlterImage
    : thumbnail || thumbnails || AlterImage;
  const videoCategory = tabTitle === 2 ? category_name || category : category;
  const videoStatus = renderContent(
    draft_id,
    content_status,
    status,
    video_id,
    transcoder_status,
    workflow_status
  );
  const videoDate = changeDateFormat(publish_date?.split(' ')[0]) || created?.split(' ')[0];
  const videoSizeFormatted = video_size < 1 && video_size > 0
      ? `${(video_size * 1024).toFixed(2)} KB`
      : video_size < 1000 && video_size > 1
      ? `${video_size} MB`
      : video_size > 1000
      ? `${(video_size / 1024).toFixed(2)} GB`
      : '--';

  const currentId = draft_id && draft_id?.slice(0, 9) !== 'old_video'
    ? draft_id
    : draft_id || content_id || draft_content_id;

  const cardRef = useRef(null);
  // Use ref for onReorderEnd so it doesn't cause useDrag to reinitialize mid-drag
  const onReorderEndRef = useRef(onReorderEnd);
  onReorderEndRef.current = onReorderEnd;

  const [{ isOverCurrent }, drop] = useDrop(() => ({
    accept: 'VIDEO_CARD',
    canDrop: () => !!canReorder,
    hover: (draggedItem, monitor) => {
      if (!canReorder || !cardRef.current) return;
      const dragIndex = draggedItem.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = cardRef.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      // Center of the hovered card
      const hoverCenterX = (hoverBoundingRect.left + hoverBoundingRect.right) / 2;
      const hoverCenterY = (hoverBoundingRect.top + hoverBoundingRect.bottom) / 2;

      // Swap when cursor passes the center — works for both horizontal and vertical
      if (dragIndex < hoverIndex) {
        if (clientOffset.x < hoverCenterX && clientOffset.y < hoverCenterY) return;
      } else if (clientOffset.x > hoverCenterX && clientOffset.y > hoverCenterY) {
        return;
      }

      moveCard(dragIndex, hoverIndex);
      // eslint-disable-next-line no-param-reassign
      draggedItem.index = hoverIndex;
    },
    collect: (monitor) => ({
      isOverCurrent: monitor.isOver({ shallow: true }),
    }),
  }), [canReorder, index, moveCard]);

const [{ isDragging }, drag] = useDrag(() => ({
  type: 'VIDEO_CARD',
  item: () => {
    if (canReorder) {
      // Reorder mode: single item with index
      setIsDragActive && setIsDragActive(true);
      return {
        id: videoId,
        video,
        title: videoTitle,
        index,
        isReorder: true,
        selectedItems: [video],
        count: 1,
      };
    }
    // Category-change mode: support multi-drag
    const allSelectedItems = selectedItems || [];
    const currentItemSelected = isItemSelected(currentId);

    let itemData;
    if (currentItemSelected && allSelectedItems.length > 1) {
      itemData = {
        id: videoId,
        video,
        title: videoTitle,
        isSelected: currentItemSelected,
        selectedItems: allSelectedItems,
        count: allSelectedItems.length
      };
    } else {
      itemData = {
        id: videoId,
        video,
        title: videoTitle,
        isSelected: currentItemSelected,
        selectedItems: [video],
        count: 1
      };
    }

    setIsDragActive && setIsDragActive(true);
    return itemData;
  },
  canDrag: () => !!canReorder,
  end: (item, monitor) => {
    setIsDragActive && setIsDragActive(false);
    if (item?.isReorder && onReorderEndRef.current) {
      // Auto-save reorder on drag end
      onReorderEndRef.current();
    }
    const dropResult = monitor.getDropResult();
    if (item && dropResult) {
      console.log('Item dropped on target', dropResult);
    }
  },
  collect: (monitor) => ({
    isDragging: !!monitor.isDragging(),
  }),
}), [videoId, video, videoTitle, currentId, isItemSelected, setIsDragActive, canReorder, selectedItems]);

  // Combine drag and drop refs
  drag(drop(cardRef));

  // In reorder mode only hide the dragged item; in category-change mode hide all selected
  const shouldHide = canReorder
    ? isDragging
    : isDragging || (isDragActive && isItemSelected(currentId));

  return (
    <div
      ref={cardRef}
      className={`${styles.videoCard} ${isSelected ? styles.selected : ''} ${isDragging ? styles.dragging : ''}`}
      style={{
        opacity: shouldHide ? 0 : 1,
        visibility: shouldHide ? 'hidden' : 'visible',
        cursor: canReorder ? (isDragging ? 'grabbing' : 'grab') : 'default',
        transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
        ...(isOverCurrent && canReorder && { borderTop: '3px solid #1a1c22' }),
      }}
    >
      <div
        className={styles.cardEditIcon}
        onClick={(e) => {
          e.stopPropagation();
          onCardClick(video);
        }}
        onKeyPress={undefined}
        role="button"
        tabIndex={0}
        title="Edit"
      >
        <EditIcon style={{ fontSize: 18, color: '#fff' }} />
      </div>
      <div className={styles.cardHeader}>
        {draft_content_id || content_id || video_id ? (
          <SquareCheckbox
            selected={isItemSelected(currentId)}
            onClick={() => {
              // For albums (tabTitle === 1), use content_id
              if (tabTitle === 1) {
                toggleItemSelection(content_id, 'content_id', undefined, video);
              } else if (draft_id && draft_id?.slice(0, 9) !== 'old_video') {
                toggleItemSelection(draft_id, 'draft_id', status, video);
              } else {
                toggleItemSelection(
                  draft_id || draft_content_id,
                  'draft_id',
                  status,
                  video
                );
              }
              setEditItems((prevSelected) => {
                const idToUse = tabTitle === 0
                    ? content_id
                    : tabTitle === 2 && draft_content_id;
                if (prevSelected.includes(idToUse)) {
                  return prevSelected.filter((id) => id !== idToUse);
                }
                return [...prevSelected, idToUse];
              });
              setEditItemsStatus((prevSelectedStatus) => {
                const idToUse = status;
                if (prevSelectedStatus.includes(idToUse)) {
                  return prevSelectedStatus.filter((id) => id !== idToUse);
                }
                return [...prevSelectedStatus, idToUse];
              });
            }}
          />
        ) : (
          <Spinner />
        )}
      </div>
      <div
        className={styles.cardContent}
      >
        <div className={styles.thumbnailContainer}>
          <img
            src={videoThumbnail}
            alt={videoTitle}
            className={styles.thumbnail}
            loading="lazy"
            decoding="async"
            onError={(e) => {
              e.target.src = AlterImage;
            }}
          />
        </div>
        <div className={styles.cardInfo}>
          <TruncatedCardTitle title={videoTitle} className={styles.cardTitle} />
          <p className={styles.cardDescription}>{videoDescription}</p>
          <div className={styles.cardMeta}>
            {videoCategory && videoCategory !== '--' && (
              <TruncatedChip text={videoCategory} className={styles.category} maxWidth={150} />
            )}
            {videoStatus && videoStatus !== '--' && (
              <span className={styles.status}>{videoStatus}</span>
            )}
            {videoDate && videoDate !== '--' && (
              <span className={styles.date}>{videoDate}</span>
            )}
            {videoSizeFormatted && videoSizeFormatted !== '--' && (
              <span className={styles.size}>{videoSizeFormatted}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DraggableVideoCard;
